/* ===================================================================
   Ashario Pets — header / mega menu / mobile drawer behavior.
   Vanilla JS, no deps. Loaded as a module via type="module" so the
   class names in here don't pollute global scope.
   =================================================================== */

(function () {
  "use strict";

  // -----------------------------------------------------------------
  // Sticky header: shrink + show shadow when scrolled
  // -----------------------------------------------------------------
  const stickyEl = document.querySelector("[data-ap-sticky-header]");
  if (stickyEl) {
    let lastY = window.scrollY;
    const onScroll = () => {
      const y = window.scrollY;
      stickyEl.classList.toggle("ap-header-wrapper--scrolled", y > 8);
      // Hide on scroll-down past 200px, show on scroll-up
      if (y > 200 && y > lastY) stickyEl.classList.add("ap-header-wrapper--hidden");
      else stickyEl.classList.remove("ap-header-wrapper--hidden");
      lastY = y;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
  }

  // -----------------------------------------------------------------
  // Mega menu: hover (desktop) / focus (keyboard) / tap (touch)
  // -----------------------------------------------------------------
  const megamenu = document.querySelector("[data-ap-megamenu]");
  if (megamenu) {
    const items = megamenu.querySelectorAll("[data-ap-mm-item].ap-megamenu__item--has-children");
    let openItem = null;
    let openTimer = null;
    let closeTimer = null;
    const isTouch = matchMedia("(hover: none)").matches;

    const open = (item) => {
      clearTimeout(closeTimer);
      if (openItem && openItem !== item) close(openItem);
      item.classList.add("ap-megamenu__item--open");
      const link = item.querySelector(".ap-megamenu__link");
      if (link) link.setAttribute("aria-expanded", "true");
      openItem = item;
      clampPanelToViewport(item);
    };

    // Re-position the open panel so it never extends past either edge
    // of the viewport. Without this, the very wide "Shop By Brand"
    // panel overflowed off the right side on smaller laptop viewports.
    const clampPanelToViewport = (item) => {
      const panel = item.querySelector(".ap-megamenu__panel");
      if (!panel) return;
      // Reset overrides before measuring so previous adjustment doesn't pollute
      panel.style.left = "";
      panel.style.right = "";
      // Force layout
      const margin = 16;
      const viewportW = document.documentElement.clientWidth;
      // Measure on next frame after the open class triggers transitions
      requestAnimationFrame(() => {
        const rect = panel.getBoundingClientRect();
        // Overflow on the right? — anchor to right of the item instead
        if (rect.right > viewportW - margin) {
          // Calculate the shift needed and clamp; prefer right-anchor
          panel.style.left = "auto";
          panel.style.right = "0px";
          // Re-measure; if still overflowing on the LEFT, fall back to viewport-aligned
          const r2 = panel.getBoundingClientRect();
          if (r2.left < margin) {
            panel.style.left = (margin - item.getBoundingClientRect().left) + "px";
            panel.style.right = "auto";
            // If still wider than viewport, set explicit max-width
            const r3 = panel.getBoundingClientRect();
            if (r3.right > viewportW - margin) {
              panel.style.maxWidth = (viewportW - 2 * margin) + "px";
            }
          }
        } else if (rect.left < margin) {
          // Overflow on the LEFT — push it right
          const itemLeft = item.getBoundingClientRect().left;
          panel.style.left = (margin - itemLeft) + "px";
        }
      });
    };
    const close = (item) => {
      item.classList.remove("ap-megamenu__item--open");
      const link = item.querySelector(".ap-megamenu__link");
      if (link) link.setAttribute("aria-expanded", "false");
      if (openItem === item) openItem = null;
    };
    const closeAll = () => items.forEach(close);

    // Parent labels whose top-level link must NOT navigate. Hovering /
    // tapping still opens the dropdown; only sub-items in the panel
    // are real links. Match is case-insensitive on the link's visible
    // text. The owner can extend this list as needed.
    const NON_NAVIGABLE_PARENTS = [
      "shop by protein",
      "shop by needs",
    ];
    const isNonNavigableLink = (link) => {
      const t = (link.textContent || "").trim().toLowerCase();
      return NON_NAVIGABLE_PARENTS.some((label) => t === label || t.startsWith(label));
    };

    items.forEach((item) => {
      const link = item.querySelector(".ap-megamenu__link");
      const nonNavigable = link ? isNonNavigableLink(link) : false;
      if (nonNavigable && link) {
        // ARIA: it's a button-like trigger, not a real navigation link
        link.setAttribute("role", "button");
        link.setAttribute("aria-haspopup", "true");
      }

      if (!isTouch) {
        item.addEventListener("mouseenter", () => {
          clearTimeout(openTimer);
          openTimer = setTimeout(() => open(item), 80);
        });
        item.addEventListener("mouseleave", () => {
          clearTimeout(openTimer);
          closeTimer = setTimeout(() => close(item), 150);
        });
      }

      // Tap: toggle (only block default if there's a panel and it's not yet open)
      link.addEventListener("click", (e) => {
        // Non-navigable parents: always block the link's default
        // navigation; toggle the dropdown instead.
        if (nonNavigable) {
          e.preventDefault();
          if (item.classList.contains("ap-megamenu__item--open")) {
            close(item);
          } else {
            open(item);
          }
          return;
        }
        if (isTouch || (e.target.closest(".ap-megamenu__caret"))) {
          if (!item.classList.contains("ap-megamenu__item--open")) {
            e.preventDefault();
            open(item);
          } else if (e.target.closest(".ap-megamenu__caret")) {
            e.preventDefault();
            close(item);
          }
        }
      });

      // Keyboard: arrow-down / Enter opens, Escape closes
      link.addEventListener("keydown", (e) => {
        if (e.key === "ArrowDown" || e.key === "Enter") {
          e.preventDefault();
          open(item);
          const firstSub = item.querySelector(".ap-megamenu__col-heading, .ap-megamenu__sublink");
          if (firstSub) firstSub.focus();
        }
      });
    });

    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape") closeAll();
    });
    document.addEventListener("click", (e) => {
      if (!e.target.closest("[data-ap-megamenu]")) closeAll();
    });
  }

  // -----------------------------------------------------------------
  // Mobile drawer: open/close + nested sub-toggles
  // -----------------------------------------------------------------
  const drawer = document.querySelector("[data-ap-drawer]");
  const drawerToggles = document.querySelectorAll("[data-ap-drawer-toggle]");
  const drawerClosers = document.querySelectorAll("[data-ap-drawer-close]");

  const openDrawer = () => {
    if (!drawer) return;
    drawer.removeAttribute("hidden");
    requestAnimationFrame(() => drawer.classList.add("ap-mobile-drawer--open"));
    document.documentElement.style.overflow = "hidden";
    drawerToggles.forEach((b) => b.setAttribute("aria-expanded", "true"));
  };
  const closeDrawer = () => {
    if (!drawer) return;
    drawer.classList.remove("ap-mobile-drawer--open");
    document.documentElement.style.overflow = "";
    drawerToggles.forEach((b) => b.setAttribute("aria-expanded", "false"));
    setTimeout(() => drawer.setAttribute("hidden", ""), 250);
  };

  drawerToggles.forEach((b) => b.addEventListener("click", openDrawer));
  drawerClosers.forEach((b) => b.addEventListener("click", closeDrawer));
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && drawer && !drawer.hasAttribute("hidden")) closeDrawer();
  });

  // Mobile drawer sub-section toggles
  const subToggles = document.querySelectorAll("[data-ap-mobile-toggle]");
  subToggles.forEach((btn) => {
    btn.addEventListener("click", () => {
      const expanded = btn.getAttribute("aria-expanded") === "true";
      btn.setAttribute("aria-expanded", expanded ? "false" : "true");
      const sub = btn.nextElementSibling;
      if (!sub) return;
      if (expanded) {
        sub.style.maxHeight = "0px";
        setTimeout(() => sub.setAttribute("hidden", ""), 200);
      } else {
        sub.removeAttribute("hidden");
        sub.style.maxHeight = sub.scrollHeight + "px";
      }
    });
  });

  // -----------------------------------------------------------------
  // Cart count: bump animation when count changes
  // (Listens for Shopify cart updates published by other scripts.)
  // -----------------------------------------------------------------
  const cartCountEl = document.querySelector("[data-cart-count]");
  if (cartCountEl) {
    const observer = new MutationObserver(() => {
      cartCountEl.classList.remove("ap-header__cart-count--bump");
      // Force reflow so animation restarts
      void cartCountEl.offsetWidth;
      cartCountEl.classList.add("ap-header__cart-count--bump");
    });
    observer.observe(cartCountEl, { childList: true, characterData: true, subtree: true });
  }
})();
